import type { Ingredient, Recipe } from '../types';
import { isOrdinaryShoppingItem } from './unitConversion';

const MINOR_FLAVORING_PATTERN = /^(פלפל שחור|black pepper|פלפל לבן|white pepper|פלפל|pepper)$/i;

const isMinorFlavoring = (ing: Ingredient): boolean => {
    const clean = (ing.name || '').toLowerCase().trim();
    if (!clean) return true;
    if (isOrdinaryShoppingItem(clean)) return true;
    return MINOR_FLAVORING_PATTERN.test(clean);
};

/**
 * Builds a restaurant-style subtitle from a recipe's most notable ingredients
 * (e.g. "Beef ribs, red wine, garlic confit"), skipping pantry staples like
 * salt, oil, and water.
 */
export function buildMenuDescription(recipe: Pick<Recipe, 'ingredients'>, maxItems: number = 4): string {
    const ingredients = recipe.ingredients || [];
    if (ingredients.length === 0) return '';

    const meaningful = ingredients.filter((ing) => !isMinorFlavoring(ing));
    const pool = meaningful.length > 0 ? meaningful : ingredients;

    const sorted = [...pool].sort((a, b) => {
        if (a.isMeat && !b.isMeat) return -1;
        if (!a.isMeat && b.isMeat) return 1;
        return 0;
    });

    const seen = new Set<string>();
    const picked: string[] = [];
    for (const ing of sorted) {
        const name = (ing.name || '').trim();
        const key = name.toLowerCase();
        if (!name || seen.has(key)) continue;
        seen.add(key);
        picked.push(name);
        if (picked.length >= maxItems) break;
    }

    return picked.join(', ');
}
